import { useEffect, useState } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { Badge, Banner, Button, Card, CardBody, Field, Input } from "@jotty/ui";
import { ApiError, integrations, apiClient } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";

export const Route = createFileRoute("/_authed/settings/integrations")({
  component: IntegrationsPage,
  validateSearch: (
    raw,
  ): {
    google?: string;
    google_error?: string;
    slack?: string;
    slack_error?: string;
  } => ({
    google: typeof raw.google === "string" ? raw.google : undefined,
    google_error:
      typeof raw.google_error === "string" ? raw.google_error : undefined,
    slack: typeof raw.slack === "string" ? raw.slack : undefined,
    slack_error:
      typeof raw.slack_error === "string" ? raw.slack_error : undefined,
  }),
});

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function IntegrationsPage() {
  const search = useSearch({ from: "/_authed/settings/integrations" });
  const tenants = useAuthStore((s) => s.tenants);
  const tenant = tenants[0];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--color-text)]">
          Integrations
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
          Connect delivery channels and meeting sources.
        </p>
      </div>

      {/* OAuth callback banners */}
      {search.google === "connected" ? (
        <Banner tone="success">
          Google account connected. Calendar + Drive access granted.
        </Banner>
      ) : null}
      {search.google_error ? (
        <Banner tone="danger">
          Google connect failed: {search.google_error.replace(/_/g, " ")}
        </Banner>
      ) : null}
      {search.slack === "connected" ? (
        <Banner tone="success">Slack workspace connected.</Banner>
      ) : null}
      {search.slack_error ? (
        <Banner tone="danger">
          Slack connect failed: {search.slack_error.replace(/_/g, " ")}
        </Banner>
      ) : null}

      {/* Delivery channels */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-[color:var(--color-text-muted)]">
          Delivery channels
        </h2>
        <EmailCard />
        {tenant ? <SlackCard tenantId={tenant.id} /> : null}
        <GoogleDocsCard />
      </section>

      {/* Sources */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-[color:var(--color-text-muted)]">
          Sources
        </h2>
        <GoogleCalendarCard />
        <p className="text-sm">
          Jotty for Mac captures supported meetings when you start recording. No
          bot joins.
        </p>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Integration card shell
// ---------------------------------------------------------------------------

function IntegrationCard({
  icon,
  name,
  subtitle,
  status,
  statusTone,
  defaultLine,
  action,
  managePanel,
}: {
  icon: React.ReactNode;
  name: string;
  subtitle: string;
  status: string;
  statusTone: "success" | "warning" | "danger" | "neutral";
  defaultLine?: string;
  action: React.ReactNode;
  managePanel?: React.ReactNode;
}) {
  const [managing, setManaging] = useState(false);

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[color:var(--color-bg)] text-lg">
              {icon}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-[color:var(--color-text)]">
                  {name}
                </p>
                <Badge tone={statusTone}>{status}</Badge>
              </div>
              <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">
                {subtitle}
              </p>
              {defaultLine ? (
                <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
                  {defaultLine}
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {managePanel ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setManaging((v) => !v)}
              >
                {managing ? "Close" : "Manage"}
              </Button>
            ) : null}
            {action}
          </div>
        </div>
        {managing && managePanel ? (
          <div className="border-t border-[color:var(--color-border)] pt-3">
            {managePanel}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Email — always connected
// ---------------------------------------------------------------------------

function EmailCard() {
  return (
    <IntegrationCard
      icon="✉️"
      name="Email"
      subtitle="Send bilingual summary emails to all meeting attendees via Infobip."
      status="connected"
      statusTone="success"
      defaultLine="Reply-to: meeting owner"
      action={
        <Button variant="secondary" size="sm" disabled>
          Managed by Kugie
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Slack
// ---------------------------------------------------------------------------

function SlackCard({ tenantId }: { tenantId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [channelId, setChannelId] = useState("");
  const [channelName, setChannelName] = useState("");
  const [connected, setConnected] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await integrations.slack.getChannel(tenantId);
        if (!cancelled) {
          setConnected(!!res.channel);
          setChannelId(res.channel?.channel_id ?? "");
          setChannelName(res.channel?.channel_name ?? "");
        }
      } catch {
        setError("Could not refresh connection. Please retry.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const handleInstall = async () => {
    setError(null);
    setBusy(true);
    try {
      const { authorization_url } = await integrations.slack.install(tenantId);
      window.location.href = authorization_url;
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not start Slack install",
      );
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setBusy(true);
    try {
      await integrations.slack.disconnect(tenantId);
      setConnected(false);
      setChannelId("");
      setChannelName("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveChannel = async () => {
    if (!channelId.trim()) return;
    setSaved(false);
    try {
      const res = await integrations.slack.putChannel(tenantId, {
        channel_id: channelId.trim(),
        channel_name: channelName.trim(),
      });
      setChannelName(res.channel?.channel_name ?? channelName);
      setSaved(true);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not save channel",
      );
    }
  };

  return (
    <IntegrationCard
      icon="💬"
      name="Slack"
      subtitle="Post Block Kit summary messages to a workspace channel."
      status={connected ? "connected" : "not connected"}
      statusTone={connected ? "success" : "neutral"}
      defaultLine={
        connected && channelName ? `Default → #${channelName}` : undefined
      }
      action={
        connected ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleDisconnect()}
            disabled={busy}
          >
            Disconnect
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => void handleInstall()}
            disabled={busy}
          >
            Connect
          </Button>
        )
      }
      managePanel={
        connected ? (
          <div className="space-y-3">
            <Field
              label="Default channel"
              helper="Channel ID (e.g. C12345) and display name."
            >
              <div className="flex gap-2">
                <Input
                  value={channelId}
                  onChange={(e) => setChannelId(e.target.value)}
                  placeholder="C12345"
                  className="flex-1"
                />
                <Input
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  placeholder="general"
                  className="max-w-[140px]"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void handleSaveChannel()}
                  disabled={!channelId.trim()}
                >
                  Save
                </Button>
              </div>
            </Field>
            {saved ? (
              <p className="text-xs text-[color:var(--color-success)]">
                Channel saved.
              </p>
            ) : null}
            {error ? (
              <p className="text-xs text-[color:var(--color-danger)]">
                {error}
              </p>
            ) : null}
          </div>
        ) : undefined
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Google Docs
// ---------------------------------------------------------------------------

function GoogleDocsCard() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useGoogleConnection(setError);

  const handleConnect = async () => {
    setError(null);
    setBusy(true);
    try {
      const { authorization_url } = await integrations.google.connect();
      window.location.href = authorization_url;
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not start Google connect",
      );
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setBusy(true);
    try {
      await integrations.google.disconnect();
      setConnected(false);
      window.dispatchEvent(new Event("jotty-google-changed"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  return (
    <IntegrationCard
      icon="📄"
      name="Google Docs"
      defaultLine={error || undefined}
      subtitle="Create a styled Doc in the meeting owner's Drive with the full summary."
      status={connected ? "connected" : "not connected"}
      statusTone={connected ? "success" : "neutral"}
      action={
        connected ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleDisconnect()}
            disabled={busy}
          >
            Disconnect
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => void handleConnect()}
            disabled={busy}
          >
            Connect
          </Button>
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Google Calendar
// ---------------------------------------------------------------------------

function GoogleCalendarCard() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected] = useGoogleConnection(setError);

  const handleConnect = async () => {
    setError(null);
    setBusy(true);
    try {
      const { authorization_url } = await integrations.google.connect();
      window.location.href = authorization_url;
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not start Google connect",
      );
      setBusy(false);
    }
  };

  return (
    <IntegrationCard
      icon="📅"
      name="Google Calendar"
      defaultLine={error || undefined}
      subtitle="See upcoming meeting links in Jotty for Mac. Start capture yourself; no bot joins."
      status={connected ? "connected" : "not connected"}
      statusTone={connected ? "success" : "neutral"}
      action={
        connected ? (
          <Button variant="secondary" size="sm" disabled={busy}>
            Connected
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => void handleConnect()}
            disabled={busy}
          >
            Connect
          </Button>
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Google connection is read from the authenticated API, never browser storage.
function useGoogleConnection(
  setError: (message: string | null) => void,
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    let active = true;
    const refresh = () => {
      void apiClient
        .get<{ connected: boolean }>("/api/v1/integrations/google/status")
        .then((r) => {
          if (active) {
            setConnected(r.connected);
            setError(null);
          }
        })
        .catch(() => {
          if (active) {
            setConnected(false);
            setError("Could not verify Google access. Reconnect or try again.");
          }
        });
    };
    refresh();
    window.addEventListener("jotty-google-changed", refresh);
    return () => {
      active = false;
      window.removeEventListener("jotty-google-changed", refresh);
    };
  }, [setError]);
  return [connected, setConnected];
}
